import React, { useEffect, useState } from 'react';
import PointsLineChart from '../../components/pointsLineChart';
import { Row, Typography } from 'antd';
import { colors } from '../../Theme/Colors';
import { DashBoardCard } from '../../components/dashBoardCard';
import { DatePickerPopUp } from '../../components/datePicker';
import { FrequentScannedCodes, HomeData } from '../../types';
import { convertToArabicNumbers } from '../../Helpers';
import { LoadingOutlined } from '@ant-design/icons';
import {
  GetGraphDataAction,
  GetMostScannedProductAction,
} from '../../Actions/ConfigActions';
import dayjs from 'dayjs';
import { useTranslate } from '@refinedev/core';

export const DashboardPage = () => {
  const translate = useTranslate();
  const [selectedDates, setSelectedDates] = useState<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    fromDate: null,
    toDate: null,
  });
  const [graphData, setGraphData] = useState<HomeData | null>(null);
  const [mostScannedProduct, setMostScannedProduct] =
    useState<FrequentScannedCodes | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const displayNumber = (number: string | number) => {
    const isRTL = document.documentElement.dir === 'rtl';
    return !isRTL ? number : convertToArabicNumbers(number.toString());
  };

  const handleDateChange = (dates: Date[]) => {
    setSelectedDates({
      fromDate: dayjs(dates[0]).toISOString(),
      toDate: dayjs(dates[1]).toISOString(),
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (selectedDates.fromDate && selectedDates.toDate) {
          const data = await GetGraphDataAction(
            selectedDates.fromDate,
            selectedDates.toDate
          );
          setGraphData(data as HomeData);
        } else {
          const data = await GetGraphDataAction();
          setGraphData(data as HomeData);
        }
        const productData = await GetMostScannedProductAction();
        setMostScannedProduct(productData as FrequentScannedCodes);
      } catch (error) {
        return error;
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      // Cleanup code if necessary
    };
  }, [selectedDates]);

  return (
    <Row style={{ backgroundColor: 'white', gap: '3rem', padding: '3rem' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ gap: '1.5rem', display: 'flex', flexDirection: 'row' }}>
            <Typography.Text
              style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                fontStyle: 'normal',
                color: colors.tableConten,
                lineHeight: 'normal',
              }}
            >
              {translate('common.pointsEarned')}
            </Typography.Text>
            <div>
              <Typography.Text
                style={{
                  color: colors.primaryColor,
                  fontSize: '2rem',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  lineHeight: 'normal',
                }}
              >
                {isLoading ? (
                  <LoadingOutlined />
                ) : (
                  displayNumber(graphData?.totalEarnedPoints ?? 0)
                )}
              </Typography.Text>
              <sub
                className="font-semibold ms-1 text-sm"
                style={{ color: colors.primaryColor }}
              >
                {translate('common.point')}
              </sub>
            </div>
          </div>
          <DatePickerPopUp
            onDateChange={handleDateChange}
            datesObject={selectedDates}
          />
        </div>
        {graphData !== null && <PointsLineChart data={graphData?.data || []} />}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '3rem',
          width: '100%',
          flexWrap: 'nowrap',
          alignSelf: 'stretch',
        }}
      >
        <DashBoardCard
          value={
            isLoading ? (
              <LoadingOutlined />
            ) : (
              displayNumber(graphData?.redemptionRate?.replace('%', '') ?? 0)
            )
          }
          percent
          title={translate('common.redemptionRate')}
        />
        <DashBoardCard
          value={
            isLoading ? (
              <LoadingOutlined />
            ) : (
              displayNumber(graphData?.consumers_count ?? 0)
            )
          }
          title={translate('common.consumer')}
        />
      </div>
      <DashBoardCard
        value={
          isLoading ? (
            <LoadingOutlined />
          ) : (mostScannedProduct?.FrequentScannedCodes ?? []).length > 0 ? (
            displayNumber(
              (mostScannedProduct?.FrequentScannedCodes ?? [])[0].NumberOfScans
            )
          ) : (
            '0'
          )
        }
        title={
          <div className="flex flex-row items-center gap-2">
            <span>{translate('dashboard.mostScannedProduct')}</span>
            <span>/</span>
            <span className="text-secondary">
              {(mostScannedProduct?.FrequentScannedCodes ?? []).length > 0
                ? mostScannedProduct?.FrequentScannedCodes[0].ProductName
                : ''}
            </span>
          </div>
        }
        width="100%"
      />
    </Row>
  );
};
